import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FlyerPublicationStatus,
  GfFlyer,
  GfImage,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiServiceRaw } from "../../../utils/api";
import { reviewKeys } from "./queryKeys";

export interface FlyerSnapshot {
  id: string;
  validStartDate?: Date;
  validEndDate?: Date;
  publishDate?: Date;
  publicationStatus?: FlyerPublicationStatus;
  pdf?: string;
  pdfLabel: string;
  coverUrl?: string;
  storeLabels: string;
  merchantLabels: string;
  tagLabel?: string;
  flyerItemListId?: string;
}

const pdfDisplayName = (pdf?: string): string => {
  if (!pdf?.trim()) return "";
  const trimmed = pdf.trim();
  try {
    const withoutQuery = trimmed.split("?")[0] ?? trimmed;
    const segment = withoutQuery.split("/").pop();
    return decodeURIComponent(segment || trimmed);
  } catch {
    return trimmed;
  }
};

const pickCover = (flyer: {
  gfImages?: Array<GfImage>;
}): string | undefined => {
  const first = flyer.gfImages?.find((img) => img?.src);
  return first?.src;
};

const toSnapshot = (flyer: GfFlyer): FlyerSnapshot => {
  const stores = flyer.stores || [];
  const storeLabels = stores
    .map((s) => s?.storeTag?.[0]?.name || "")
    .filter(Boolean)
    .join(", ");
  const merchantLabels = stores
    .map((s) => s?.merchant?.name || "")
    .filter(Boolean)
    .join(", ");
  const tagLabel = flyer.flyerTag?.[0]?.name;
  return {
    id: flyer.id,
    validStartDate: flyer.validStartDate,
    validEndDate: flyer.validEndDate,
    publishDate: flyer.publishDate,
    publicationStatus: flyer.publicationStatus,
    pdf: flyer.pdf,
    pdfLabel: pdfDisplayName(flyer.pdf),
    coverUrl: pickCover(flyer),
    storeLabels,
    merchantLabels,
    tagLabel,
    flyerItemListId: flyer.flyerItemList?.id,
  };
};

export const useFlyerSnapshot = (
  flyerId: string | undefined,
  authHeaders: { headers: { Authorization: string } },
) => {
  const authToken = authHeaders.headers.Authorization;

  const query = useQuery({
    queryKey: reviewKeys.snapshot(flyerId ?? ""),
    queryFn: ({ signal }) =>
      adminApiServiceRaw.getOneBaseGfFlyerAdminControllerGfFlyer(
        (flyerId as string).trim(),
        undefined,
        undefined,
        undefined,
        { headers: { Authorization: authToken }, signal },
      ),
    enabled: !!flyerId,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // FlyerMetaBar renders its own inline error + retry, so don't also toast.
    meta: { suppressGlobalError: true },
    select: (row): FlyerSnapshot | undefined => {
      const flyer = row as GfFlyer | undefined;
      return flyer?.id ? toSnapshot(flyer) : undefined;
    },
  });

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query]);

  return {
    snapshot: query.data,
    // Preserve the previous behavior where the skeleton showed on refetch too.
    loading: query.isFetching,
    // "no id in row" was treated as an error by the previous implementation.
    error: query.isError || (query.isSuccess && query.data === undefined),
    refetch,
  };
};
